/** @jsx jsx */
/* eslint-disable react/no-unknown-property */
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import ReactDOM from "react-dom";
import Axios from "axios";
import { jsx, css, keyframes, useTheme } from "@emotion/react";

const CATS_ID_PATTERN = /^\d{2}-[A-Z0-9]+-\d{6,}$/i;

export function filterExactCatsIdOptions(options, query) {
    const normalizedQuery = String(query || "").trim().toUpperCase();

    if (!CATS_ID_PATTERN.test(normalizedQuery)) {
        return options;
    }

    return options.filter(option => {
        const label = String(option.label || "");
        const assetCode = label.split(" - ")[0]?.trim().toUpperCase();

        return assetCode === normalizedQuery;
    });
}

const spin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const DEFAULT_DEBOUNCE_MS = 300;

const LegacyTypeahead = ({
    name,
    value,
    placeholder = "Type to Search",
    disabled = false,
    searchUrl,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minLength = 1,
    dropdownWidth = "anchor",
    allowCreate = false,
    createLabel = "Create new",
    className = "",
    inputClassName = "",
    menuClassName = "",
    filterOptions = filterExactCatsIdOptions,
    resultIcon: ResultIcon,
    renderOption,
    renderActions,
    onSelect,
    onClear,
    onInputChange,
    onError
}) => {
    const theme = useTheme();
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const optionRefs = useRef([]);
    const requestIdRef = useRef(0);
    const hasUserInputRef = useRef(false);

    const normalizedValue =
        value && value.value !== undefined && value.value !== null ?
            value :
            null;
    const [inputValue, setInputValue] = useState(normalizedValue?.label || "");
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [menuPosition, setMenuPosition] = useState(null);

    useEffect(() => {
        hasUserInputRef.current = false;
        setInputValue(normalizedValue?.label || "");
    }, [normalizedValue?.label, normalizedValue?.value]);

    const updateMenuPosition = useCallback(() => {
        const node = wrapperRef.current;
        if (!node) {
            return;
        }

        const rect = node.getBoundingClientRect();
        const viewportPadding = 16;
        const preferredWidth = dropdownWidth === "full" ||
            dropdownWidth === "anchor" ?
                rect.width :
                Number(dropdownWidth);
        const width = Math.min(
            Math.max(220, preferredWidth),
            window.innerWidth - viewportPadding * 2
        );
        const left = Math.min(
            Math.max(rect.left, viewportPadding),
            window.innerWidth - width - viewportPadding
        );

        setMenuPosition({
            top: rect.bottom + 4,
            left,
            width
        });
    }, [dropdownWidth]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        updateMenuPosition();
        window.addEventListener("resize", updateMenuPosition);
        window.addEventListener("scroll", updateMenuPosition, true);

        return () => {
            window.removeEventListener("resize", updateMenuPosition);
            window.removeEventListener("scroll", updateMenuPosition, true);
        };
    }, [isOpen, updateMenuPosition]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handlePointerDown = event => {
            if (
                wrapperRef.current?.contains(event.target) ||
                menuRef.current?.contains(event.target)
            ) {
                return;
            }

            setIsOpen(false);
            setActiveIndex(-1);
        };

        document.addEventListener("mousedown", handlePointerDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, [isOpen]);

    useEffect(() => {
        const trimmedValue = inputValue.trim();

        if (
            disabled ||
            !hasUserInputRef.current ||
            trimmedValue.length < minLength ||
            !searchUrl
        ) {
            setOptions([]);
            setIsLoading(false);
            return undefined;
        }

        const currentRequestId = requestIdRef.current + 1;
        requestIdRef.current = currentRequestId;

        const timeout = setTimeout(async() => {
            setIsLoading(true);

            try {
                let url = searchUrl(trimmedValue);

                if (
                    url &&
                    !url.startsWith("/") &&
                    !url.startsWith("http://") &&
                    !url.startsWith("https://")
                ) {
                    url = `/${url}`;
                }

                const response = await Axios.get(url);

                if (requestIdRef.current !== currentRequestId) {
                    return;
                }

                const responseOptions = Array.isArray(response.data) ?
                    response.data :
                    [];
                setOptions(filterOptions(responseOptions, trimmedValue));
                setIsOpen(true);
            } catch (error) {
                if (requestIdRef.current === currentRequestId) {
                    setOptions([]);
                    onError?.(error);
                }
            } finally {
                if (requestIdRef.current === currentRequestId) {
                    setIsLoading(false);
                }
            }
        }, debounceMs);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        debounceMs,
        disabled,
        filterOptions,
        inputValue,
        minLength,
        onError,
        searchUrl
    ]);

    const createOption = useMemo(() => {
        const trimmedValue = inputValue.trim();

        if (!allowCreate || isLoading || !trimmedValue) {
            return null;
        }

        return {
            value: trimmedValue,
            label:
                typeof createLabel === "function" ?
                    createLabel(trimmedValue) :
                    createLabel,
            __isNew__: true
        };
    }, [allowCreate, createLabel, inputValue, isLoading]);

    const visibleOptions = createOption ? [...options, createOption] : options;

    const handleSelect = useCallback(
        option => {
            const action = option.__isNew__ ? "create-option" : "select-option";

            hasUserInputRef.current = false;
            setInputValue(option.__isNew__ ? inputValue : option.label || "");
            setIsOpen(false);
            setActiveIndex(-1);
            onSelect?.(option, { action });
        },
        [inputValue, onSelect]
    );

    const handleClear = useCallback(() => {
        hasUserInputRef.current = false;
        setInputValue("");
        setOptions([]);
        setIsOpen(false);
        setActiveIndex(-1);
        onClear?.();
    }, [onClear]);

    const handleInputChange = event => {
        const nextValue = event.target.value;
        hasUserInputRef.current = true;
        setInputValue(nextValue);
        setIsOpen(Boolean(nextValue));
        setActiveIndex(-1);
        onInputChange?.(nextValue);
    };

    const handleKeyDown = event => {
        if (disabled) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex(current =>
                Math.min(current + 1, visibleOptions.length - 1)
            );
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(current =>
                current <= 0 ? visibleOptions.length - 1 : current - 1
            );
            return;
        }

        if (event.key === "Enter") {
            if (isOpen && activeIndex >= 0 && visibleOptions[activeIndex]) {
                event.preventDefault();
                handleSelect(visibleOptions[activeIndex]);
            }
            return;
        }

        if (event.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
        }
    };

    useEffect(() => {
        if (activeIndex < 0) {
            return;
        }

        optionRefs.current[activeIndex]?.scrollIntoView({
            block: "nearest"
        });
    }, [activeIndex]);

    const styles = css`
        position: relative;
        display: flex;
        align-items: stretch;
        width: 100%;

        .legacy-typeahead-input-wrap {
            position: relative;
            flex: 1 1 auto;
            min-width: 0;
        }

        .legacy-typeahead-input {
            width: 100%;
            height: 38px;
            border: 1px solid ${theme.colors.grayBorder};
            border-radius: 4px;
            padding: 0.375rem ${isLoading || inputValue ? "2.5rem" : "0.75rem"}
                0.375rem 0.75rem;
            color: ${theme.colors.grayDark};
            background: ${disabled ? theme.colors.grayDisabled : theme.colors.white};
            outline: none;

            &:focus {
                border-color: #80bdff;
                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
            }

            &::placeholder {
                color: ${theme.colors.gray};
            }
        }

        .legacy-typeahead-input:disabled {
            cursor: not-allowed;
            color: ${theme.colors.grayDisabledText};
        }

        .legacy-typeahead-status,
        .legacy-typeahead-clear {
            position: absolute;
            top: 50%;
            right: 0.25rem;
            transform: translateY(-50%);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
        }

        .legacy-typeahead-spinner {
            width: 14px;
            height: 14px;
            border: 2px solid ${theme.colors.grayBorder};
            border-top-color: ${theme.colors.primary};
            border-radius: 50%;
            animation: ${spin} 0.8s linear infinite;
        }

        .legacy-typeahead-clear {
            border: 0;
            padding: 0;
            background: transparent;
            color: ${theme.colors.gray};
            cursor: pointer;
            font-size: 1.25rem;
            font-weight: 400;
            line-height: 1;

            &:hover {
                color: ${theme.colors.grayDark};
            }
        }
    `;

    const menuStyles = css`
        position: fixed;
        z-index: 2147483000;
        max-height: 300px;
        overflow-y: auto;
        background: ${theme.colors.white};
        border: 1px solid #aeb6bf;
        border-radius: 8px;
        box-shadow: 0 0.75rem 2rem rgba(33, 37, 41, 0.16);
        font-size: 0.875rem;

        .legacy-typeahead-option,
        .legacy-typeahead-empty {
            padding: 0.625rem 0.875rem;
        }

        .legacy-typeahead-option {
            display: grid;
            grid-template-columns: ${ResultIcon ? "24px minmax(0, 1fr)" : "minmax(0, 1fr)"};
            column-gap: 0.75rem;
            align-items: center;
            min-height: 48px;
            color: ${theme.colors.grayDark};
            cursor: pointer;
            border-bottom: 1px solid #edf0f2;
            transition:
                background-color 0.2s ease,
                box-shadow 0.2s ease;

            &:hover,
            &.active {
                background: #f2f4f6;
            }

            &.active {
                box-shadow: inset 3px 0 0 ${theme.colors.primary};
            }

            &:last-of-type {
                border-bottom: 0;
            }
        }

        .legacy-typeahead-option-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${theme.colors.grayDisabledText};
        }

        .legacy-typeahead-option-label {
            min-width: 0;
            line-height: 1.35;
            font-weight: 500;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .legacy-typeahead-option.active .legacy-typeahead-option-icon {
            color: ${theme.colors.grayDark};
        }

        .legacy-typeahead-option-create {
            font-weight: 600;
        }

        .legacy-typeahead-option-create .legacy-typeahead-option-icon {
            color: ${theme.colors.primary};
        }

        .legacy-typeahead-empty {
            border: 0;
            min-height: 42px;
        }

        @media (max-width: 640px) {
            .legacy-typeahead-option {
                min-height: 44px;
                padding: 0.5rem 0.75rem;
                grid-template-columns: ${ResultIcon ? "22px minmax(0, 1fr)" : "minmax(0, 1fr)"};
            }
        }

        .legacy-typeahead-empty {
            color: ${theme.colors.gray};
            font-style: italic;
            text-align: center;
        }
    `;

    return (
        <Fragment>
            <div css={styles} className={className} ref={wrapperRef}>
                <div className="legacy-typeahead-input-wrap">
                    <input
                        ref={inputRef}
                        className={`legacy-typeahead-input ${inputClassName}`}
                        autoComplete="off"
                        disabled={disabled}
                        name={name}
                        onChange={handleInputChange}
                        onFocus={() => {
                            if (inputValue) {
                                setIsOpen(true);
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        type="text"
                        value={inputValue}
                    />
                    {isLoading && (
                        <span className="legacy-typeahead-status">
                            <span className="legacy-typeahead-spinner" />
                        </span>
                    )}
                    {!isLoading && inputValue && !disabled && (
                        <button
                            aria-label="Clear search"
                            className="legacy-typeahead-clear"
                            onClick={handleClear}
                            type="button"
                        >
                            &times;
                        </button>
                    )}
                </div>
                {renderActions?.(normalizedValue)}
            </div>
            {isOpen &&
                menuPosition &&
                ReactDOM.createPortal(
                    <div
                        css={menuStyles}
                        className={menuClassName}
                        ref={menuRef}
                        style={{
                            left: menuPosition.left,
                            top: menuPosition.top,
                            width: menuPosition.width
                        }}
                    >
                        {visibleOptions.length === 0 && !isLoading ? (
                            <div className="legacy-typeahead-empty">
                                {inputValue ? "No results found" : "Type to search..."}
                            </div>
                        ) : (
                            visibleOptions.map((option, index) => (
                                <div
                                    className={`legacy-typeahead-option ${
                                        activeIndex === index ? "active" : ""
                                    } ${
                                        option.__isNew__ ?
                                            "legacy-typeahead-option-create" :
                                            ""
                                    }`}
                                    key={`${option.value}-${index}`}
                                    onMouseDown={event => {
                                        event.preventDefault();
                                        handleSelect(option);
                                    }}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    ref={node => {
                                        optionRefs.current[index] = node;
                                    }}
                                    role="option"
                                >
                                    {ResultIcon && (
                                        <span className="legacy-typeahead-option-icon">
                                            <ResultIcon
                                                size={18}
                                                weight={option.__isNew__ ? "bold" : "fill"}
                                            />
                                        </span>
                                    )}
                                    <span className="legacy-typeahead-option-label">
                                        {renderOption ?
                                            renderOption(option) :
                                            option.label}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>,
                    document.body
                )}
        </Fragment>
    );
};

export default LegacyTypeahead;
